// client/src/pages/FormSignPage.js — where a client signs.
//
// No account, no login: the link itself is the authority, it works once, and it
// expires. That is deliberate — asking a buyer to create a password before they
// can sign a disclosure is how paper wins.
//
// The consent box is not decoration. Under E-SIGN (15 U.S.C. 7001) an electronic
// signature carries the weight of ink only where the signer agreed to do business
// electronically, so the agreement is recorded with the signature, along with the
// time, the address it came from and the browser that sent it.
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const FormSignPage = () => {
    const { token } = useParams();
    const [doc, setDoc] = useState(null);
    const [error, setError] = useState('');
    const [typed, setTyped] = useState('');
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await fetch(`/api/forms-engine/sign/${token}`);
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'This link is not valid.');
            setDoc(data);
            setTyped(data.signer_name || '');
        } catch (err) {
            setError(err.message);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const res = await fetch(`/api/forms-engine/sign/${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ typed_name: typed, consent }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Could not record the signature.');
            setDone(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <div className="max-w-lg w-full bg-white border rounded-lg shadow p-8 text-center">
                    <h1 className="text-2xl font-semibold text-slate-800 mb-2">Signed</h1>
                    <p className="text-slate-600">
                        Thank you. A copy has been filed with your broker and will appear in your portal.
                    </p>
                </div>
            </div>
        );
    }

    if (error && !doc) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <div className="max-w-lg w-full bg-white border rounded-lg shadow p-8">
                    <h1 className="text-xl font-semibold text-slate-800 mb-2">This link cannot be opened</h1>
                    <p className="text-slate-600">{error}</p>
                </div>
            </div>
        );
    }

    if (!doc) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

    return (
        <div className="min-h-screen bg-slate-50 py-8 px-4">
            <div className="max-w-3xl mx-auto">
                <div className="bg-white border rounded-lg shadow">
                    <div className="px-6 py-4 border-b">
                        <h1 className="text-xl font-semibold text-slate-800">{doc.title}</h1>
                        {doc.statutory_ref && <p className="text-xs text-slate-500 mt-0.5">{doc.statutory_ref}</p>}
                        <p className="text-sm text-slate-600 mt-1">
                            Prepared for {doc.signer_name}. Read it in full before signing.
                        </p>
                    </div>

                    <div className="px-6 py-5 max-h-[55vh] overflow-y-auto prose prose-sm max-w-none">
                        {doc.document_html
                            // The document is generated by this application from its own
                            // templates, not supplied by any user.
                            ? <div dangerouslySetInnerHTML={{ __html: doc.document_html }} />
                            : (
                                <p className="text-slate-600">
                                    The document was sent to you separately.{' '}
                                    {doc.download_url && (
                                        <a className="text-blue-600 underline" href={doc.download_url}
                                           target="_blank" rel="noreferrer">Open it here</a>
                                    )}
                                </p>
                            )}
                    </div>

                    <form onSubmit={submit} className="px-6 py-5 border-t bg-slate-50 rounded-b-lg">
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Type your full legal name to sign
                        </label>
                        <input value={typed} onChange={(e) => setTyped(e.target.value)}
                               className="w-full p-3 border rounded text-lg"
                               style={{ fontFamily: 'Georgia, serif' }} required />

                        <label className="flex items-start gap-2 mt-4 text-sm text-slate-700">
                            <input type="checkbox" className="mt-1" checked={consent}
                                   onChange={(e) => setConsent(e.target.checked)} />
                            <span>{doc.consent_text}</span>
                        </label>

                        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

                        <button type="submit" disabled={busy || !consent || !typed.trim()}
                                className="mt-4 px-5 py-2.5 rounded bg-blue-600 text-white font-medium
                                           hover:bg-blue-700 disabled:opacity-50">
                            {busy ? 'Signing…' : 'Sign and submit'}
                        </button>
                        <p className="mt-3 text-xs text-slate-500">
                            Prefer to sign on paper? Ask your broker to send a printed copy — it is filed the same way.
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default FormSignPage;
